import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtUsd } from "@/lib/api";
import { Hint } from "@/components/Hint";
import { hint as glossary } from "@/lib/glossary";

export const KINDS = [
  { v: "price_above", l: "Price above (USD)" },
  { v: "price_below", l: "Price below (USD)" },
  { v: "range_enter", l: "Price enters range" },
  { v: "range_exit", l: "Price leaves range" },
  { v: "sr_break", l: "Support / resistance break" },
  { v: "volume_spike", l: "Volume spike (× 30-day avg)" },
  { v: "volatility", l: "24h volatility above (%)" },
  { v: "signal_change", l: "AI signal change" },
];
export const KIND_HINT = { price_above: "price_above", price_below: "price_below", range_enter: "range_enter", range_exit: "range_exit", sr_break: "sr_break", volume_spike: "volume_spike", volatility: "volatility_alert", signal_change: "signal_change", signal_conditions: "signal_conditions" };
export const KIND_LABELS = { ...Object.fromEntries(KINDS.map((k) => [k.v, k.l])), signal_conditions: "Signal waiting conditions" };
const inputCls = "apx-ring px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-800 outline-none text-sm disabled:opacity-50 w-full";

export const CreateAlertForm = ({ onCreated }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [kind, setKind] = useState("price_above");
  const [threshold, setThreshold] = useState("");
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");
  const [side, setSide] = useState("support");
  const [levels, setLevels] = useState(null);
  const [expires, setExpires] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get("/market/search", { params: { q: search } }).then((r) => setResults((r.data.coins || []).slice(0, 6))), 220);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!picked || kind !== "sr_break") { setLevels(null); return; }
    api.get(`/alerts/levels/${picked.id}`).then((r) => setLevels(r.data)).catch(() => setLevels({ error: true }));
  }, [picked, kind]);

  const needsThreshold = ["price_above", "price_below", "volume_spike", "volatility"].includes(kind);
  const isRange = kind === "range_enter" || kind === "range_exit";

  const create = async () => {
    if (!picked) { toast.error("Pick a coin"); return; }
    if (needsThreshold && !threshold) { toast.error("Enter a threshold"); return; }
    if (isRange && (!low || !high || Number(low) >= Number(high))) { toast.error("Enter a valid range (low < high)"); return; }
    setBusy(true);
    try {
      await api.post("/alerts", {
        coin_id: picked.id, symbol: picked.symbol, name: picked.name, kind, expires_days: expires, note: note || null,
        threshold: needsThreshold ? Number(threshold) : null,
        low: isRange ? Number(low) : null, high: isRange ? Number(high) : null,
        side: kind === "sr_break" ? side : null,
      });
      toast.success("Alert created — watched live and every 15 minutes");
      setPicked(null); setSearch(""); setThreshold(""); setLow(""); setHigh(""); setNote("");
      onCreated?.();
    } catch (e) { toast.error(apiError(e, "Could not create alert")); }
    setBusy(false);
  };

  return (
    <div className="apx-card p-5" data-testid="create-alert-card">
      <div className="font-display font-semibold mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-cyan-300" /> Create alert</div>
      <div className="grid md:grid-cols-6 gap-3">
        <div className="md:col-span-2 relative">
          <input data-testid="alert-coin-search" value={picked ? `${picked.name} (${picked.symbol?.toUpperCase()})` : search}
                 onChange={(e) => { setPicked(null); setSearch(e.target.value); }} placeholder="Search coin…" className={inputCls} />
          {results.length > 0 && !picked && (
            <div className="absolute z-10 mt-1 left-0 right-0 apx-glass rounded-lg overflow-hidden max-h-56 overflow-y-auto">
              {results.map((c) => (
                <button key={c.id} data-testid={`alert-pick-${c.id}`} onClick={() => { setPicked(c); setResults([]); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-800/50 text-left text-sm">
                  {c.thumb && <img src={c.thumb} className="w-5 h-5 rounded-full" alt="" />}<span className="font-medium">{c.name}</span><span className="text-xs text-slate-500 uppercase">{c.symbol}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Hint k={KIND_HINT[kind]} side="bottom" asChild>
          <select data-testid="alert-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls + " md:col-span-2"}>
            {KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
          </select>
        </Hint>
        {needsThreshold && (
          <input data-testid="alert-threshold" type="number" step="0.0001" value={threshold} onChange={(e) => setThreshold(e.target.value)}
                 placeholder={kind === "volume_spike" ? "e.g. 2 (× avg)" : kind === "volatility" ? "e.g. 8 (%)" : "Price (USD)"} className={inputCls} />
        )}
        {isRange && (<>
          <input data-testid="alert-low" type="number" step="0.0001" value={low} onChange={(e) => setLow(e.target.value)} placeholder="Low (USD)" className={inputCls} />
          <input data-testid="alert-high" type="number" step="0.0001" value={high} onChange={(e) => setHigh(e.target.value)} placeholder="High (USD)" className={inputCls} />
        </>)}
        {kind === "sr_break" && (
          <select data-testid="alert-side" value={side} onChange={(e) => setSide(e.target.value)} className={inputCls}>
            <option value="support">Support break (down)</option><option value="resistance">Resistance break (up)</option>
          </select>
        )}
        {kind === "signal_change" && <div className="text-xs text-slate-500 self-center" data-testid="alert-signal-change-note">Fires when the AI outlook changes</div>}
        <Hint k="alert_expiry" side="bottom" asChild>
          <select data-testid="alert-expiry" value={expires} onChange={(e) => setExpires(Number(e.target.value))} className={inputCls}>
            <option value={7}>Expires in 7 days</option><option value={30}>Expires in 30 days</option><option value={90}>Expires in 90 days</option>
          </select>
        </Hint>
        <Hint k="create_alert" side="bottom" asChild>
          <button data-testid="create-alert-btn" disabled={busy} onClick={create} className="btn-primary rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-60">Create</button>
        </Hint>
      </div>
      {kind === "sr_break" && levels && !levels.error && (
        <div className="mt-2 text-[11px] text-slate-400" data-testid="alert-sr-levels">
          Current {fmtUsd(levels.price)} · programmatic supports: {(levels.supports || []).map(fmtUsd).join(", ") || "none"} · resistances: {(levels.resistances || []).map(fmtUsd).join(", ") || "none"}. The nearest {side} level is frozen into the alert.
        </div>
      )}
      <div className="mt-2 text-[11px] text-slate-500" data-testid="alert-kind-explainer">
        <span className="text-slate-300 font-medium">{glossary(KIND_HINT[kind])?.[0]}:</span> {glossary(KIND_HINT[kind])?.[1]}
      </div>
      <input data-testid="alert-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note (e.g. 'Target entry')" className="w-full mt-3 apx-ring px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 outline-none text-xs" />
    </div>
  );
};
